import { ErrorMessage } from '@hookform/error-message'
import { Button, Input, Space, Tag, Typography, notification } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import './index.scss'

import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IDefaultResponse, IGenerateOTP, IOTPLogin, IResetPassword } from 'src/app/shared/models/auth.model'
import { generateOTP, resendOTP, resetPassword, signIn } from 'src/app/shared/services/AuthenticationService'
import { clearPreAuthenInfo, getPreAuthenInfo, increaseCountOtpSent } from 'src/app/store/preAuthen'

import { WarningFilled } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import OTPInput, { InputProps } from 'react-otp-input'
import ErrorText from 'src/app/shared/components/common/error-text'
import { defaultHomePage } from 'src/app/shared/constants/app.constants'
import { FROM_RESET_LOGIN_PAGE_STATE, FROM_RESET_PASSWORD_PAGE_STATE } from 'src/app/shared/constants/common.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { IAuthState } from 'src/app/shared/models/store.model'
import { logIn } from 'src/app/store/auth'
import { colorTextSelected } from 'src/app/themes/primaryThemeDefault'
import { OTPCountDown } from './otp-countdown'

const OTPLogin: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const preAuthenInfo = useSelector(getPreAuthenInfo)
  const [loading, setLoading] = useState<boolean>(false)
  const [isShowCountdown, setIsShowCountdown] = useState<boolean>(false)
  const [toggleResetCountDown, setToggleResetCountDown] = useState<boolean>(false)
  const [otp, setOtp] = useState<string>('')
  const [otpDuration, setOtpDuration] = useState<number>(0)
  const [isDisableResendOtp, setIsDisableResendOtp] = useState<boolean>(false)

  const { state } = useLocation()

  const otpInputLength = 5

  const initForm: IOTPLogin = {
    userName: '',
    password: '',
    otp: '',
  }

  const {
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<IOTPLogin>({ mode: 'all', defaultValues: initForm })

  const renderOTPInput = useCallback(
    (props: InputProps, index: number) => {
      const { ref, className, type, style, ...etc } = props
      return <Input
        id={`otp-input-${index}`}
        className='otp-input-field'
        status={errors?.otp ? 'error' : ''}
        ref={ref as any}
        {...etc} />
    },
    [errors],
  )

  useEffect(() => {
    clearErrors()
    return () => { }
  }, [otp])

  const [messageTag, setMessageTag] = useState<string>('');

  const onSubmit: SubmitHandler<IOTPLogin> = (data) => {
    if (state?.from === FROM_RESET_LOGIN_PAGE_STATE) {
      const loginPayload: IOTPLogin = {
        userName: preAuthenInfo.userName ?? '',
        password: preAuthenInfo.password ?? '',
        otp: otp,
      }
      setLoading(true)
      signIn(loginPayload)
        .then(
          (res: any) => {
            const dataAuth: IAuthState = res.data
            dispatch(clearPreAuthenInfo())
            dispatch(
              logIn({ ...dataAuth, accessTokenExpiresAt: dayjs().add(dataAuth.accessExpiresIn, 'seconds').format() }),
            )
            navigate(defaultHomePage)
          },
          (err: IDefaultResponse<any>) => {
            handleErrorResponse(err)
          },
        )
        .finally(() => {
          setLoading(false)
        })
    }

    if (state?.from === FROM_RESET_PASSWORD_PAGE_STATE) {
      let resetPasswordPayload: IResetPassword = {
        userName: preAuthenInfo.userName ?? '',
        newPassword: preAuthenInfo.newPassword ?? '',
        confirmNewPassword: preAuthenInfo.confirmNewPassword ?? '',
        otp: otp,
      }

      if (preAuthenInfo.currentPassword) {
        resetPasswordPayload.currentPassword = preAuthenInfo.currentPassword
      }

      setLoading(true)
      resetPassword(resetPasswordPayload)
        .then(
          (res: any) => {
            const dataAuth: IAuthState = res.data
            dispatch(clearPreAuthenInfo())
            dispatch(
              logIn({ ...dataAuth, accessTokenExpiresAt: dayjs().add(dataAuth.accessExpiresIn, 'seconds').format() }),
            )
            navigate('/home/menu')
          },
          (err: IDefaultResponse<any>) => {
            handleErrorResponse(err)
          },
        )
        .finally(() => {
          setLoading(false)
        })
    }

    return
  }

  const handleGenerateOTP = () => {
    clearErrors()
    const payload: IGenerateOTP = {
      userName: preAuthenInfo.userName ?? '',
    }

    generateOTP(payload).then(
      (res: any) => {
        setIsShowCountdown(true)
        setOtpDuration(res?.data?.duration ?? 0)
        dispatch(increaseCountOtpSent())
        setToggleResetCountDown((value: boolean) => !value)
      },
      (err: IDefaultResponse<any>) => {
        handleErrorResponse(err)
      },
    )
  }

  const handleResendOTP = () => {
    clearErrors()
    setOtp('')
    const payload: IGenerateOTP = {
      userName: preAuthenInfo.userName ?? '',
    }
    setIsDisableResendOtp(true)
    resendOTP(payload)
      .then(
        (res: any) => {
          if (res?.data?.isSuccess) {
            setIsShowCountdown(true)
            setOtpDuration(res?.data?.duration ?? 0)
            dispatch(increaseCountOtpSent())
            setToggleResetCountDown((value: boolean) => !value)
          } else {
            notification.error({
              message: '',
              description: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PLEASE_RESEND_OTP_LATER),
            })
          }
        },
        (err: IDefaultResponse<any>) => {
          handleErrorResponse(err)
        },
      )
      .finally(() => {
        setTimeout(() => {
          setIsDisableResendOtp(false)
        }, 30 * 1000)
      })
  }

  useEffect(() => {
    handleGenerateOTP()
    return () => { }
  }, [])

  const handleOnfinishCountdownOTP = () => {
    if (otp) {
      setError('otp', {
        type: 'required',
        message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.EXPIRED_OTP),
      })
    } else {
      setError('otp', {
        type: 'required',
        message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_OTP),
      })
    }
  }

  const hideCountDownHandler = () => {
    setIsShowCountdown(false)
  }

  const handleErrorResponse = (err: IDefaultResponse<any>) => {
    switch (err?.statusCode) {
      case SYSTEM_MESSAGE_IDS.INVALID_OTP:
      case SYSTEM_MESSAGE_IDS.EXPIRED_OTP:
        setError('otp', {
          type: 'required',
          message: t('SystemMessages:' + err?.statusCode),
        })
        break

      case SERVER_STATUS_CODES.PPI5_RESEND_OTP_TOO_MANY_TIMES:
        navigate('/locked-user')
        break

      case SERVER_STATUS_CODES.PPI3_LOCK_USER:
        navigate('/locked-user')
        break

      case SERVER_STATUS_CODES.PPI4_USER_IS_INACTIVE:
        navigate('/inactive-user')
        break
      case SYSTEM_MESSAGE_IDS.PENDING_USER_EMAIL_INTERACTION:
        setMessageTag(t('LoginPage:login_pending_email_user_interaction'))
        setIsShowCountdown(false)
        break;
      default:
        break
    }
  }

  return (
    <div className='otp-login'>
      <div style={{ marginBottom: '64px', textAlign: 'center' }}>
        <Typography.Text
          style={{
            fontSize: '32px',
            fontWeight: 400,
            lineHeight: '33px',
            color: '#7B858B',
            margin: 0,
            textAlign: 'center',
          }}
        >
          The most effective safeguard <br></br> against transaction fraud.
        </Typography.Text>
        <Typography.Text
          style={{
            display: 'block',
            fontWeight: 700,
            fontSize: '28px',
            lineHeight: '33px',
            margin: '8px 0 8px 0',
          }}
        >
          OTP
        </Typography.Text>
        <Typography.Text>
          <Typography.Text type='secondary' style={{ fontSize: '16px', marginRight: '4px' }}>
            {t('LoginPage:otp_description_text')}
          </Typography.Text>
          <Typography.Text style={{ color: colorTextSelected, fontSize: '16px' }}>
            {preAuthenInfo.email?.includes('@')
              ? String(preAuthenInfo.email?.split('@')[0])?.slice(0, 3) +
              '***@' +
              String(preAuthenInfo.email?.split('@')[1])
              : preAuthenInfo.email}
          </Typography.Text>
        </Typography.Text>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='otp-card-inputs'>
          <div className='otp-card-inputs-container'>
            <OTPInput
              value={otp}
              onChange={(e: any) => {
                if (!e) {
                  setError('otp', {
                    type: 'required',
                    message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_OTP),
                  })
                }
                setOtp(e)
              }}
              renderInput={(inputProps, index) => renderOTPInput(inputProps, index)}
              numInputs={otpInputLength}
              renderSeparator={<span />}
              inputType='number'
            />
            <center>
              <ErrorMessage errors={errors} name='otp' render={({ message }) => <ErrorText>{message}</ErrorText>} />
            </center>
          </div>
        </div>

        <OTPCountDown
          isShowCountdown={isShowCountdown}
          toggleResetCountDown={toggleResetCountDown}
          durationSeconds={otpDuration}
          onFinishCallback={handleOnfinishCountdownOTP}
          hideCountDownHandler={hideCountDownHandler}
          format='mm:ss'
          prefix='Please enter OTP within'
          suffix='minutes'
        />

        <div className='otp-card-resend-container'>
          <div className='otp-card-resend-section'>
            <Space className='d-flex justify-content-center' style={{ gap: '6px' }}>
              <Typography.Text style={{ fontSize: '16px' }}>
                {t('LoginPage:otp_resend_description_text')}
              </Typography.Text>
              <Typography.Link
                style={{ fontSize: '16px' }}
                disabled={isDisableResendOtp || loading}
                underline
                onClick={() => handleResendOTP()}
                target='_blank'
              >
                {t('LoginPage:resend_btn')}.
              </Typography.Link>
            </Space>
          </div>
        </div>
        {
          messageTag?.length > 0 && (
            <Tag className='tag-item' icon={<WarningFilled style={{ fontSize: '18px' }} />} color='warning'>
              <Typography.Text style={{ fontSize: '14px' }}>{messageTag}</Typography.Text>
            </Tag>)
        }
        <Button
          size='large'
          onClick={handleSubmit(onSubmit)}
          type='primary'
          block
          disabled={otp?.length < otpInputLength || loading}
          loading={loading}
        >
          {t('common:continue_btn')}
        </Button>
      </form>
    </div>
  )
}

export default OTPLogin
